import { useState, useMemo } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import {
  CalendarDays, List, ChevronLeft, ChevronRight, Truck, Package,
  DollarSign, ShoppingBag, Filter, Check, Loader2, Inbox, Route as RouteIcon, Repeat,
  SlidersHorizontal, X, Store, Warehouse, CreditCard, PackageCheck, Search, ClipboardCheck, Timer, Info,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { StatusBadge } from "@/components/recon/StatusBadge";
import { OrderTypeBadge } from "@/components/recon/OrderTypeBadge";
import { ReservaBadge } from "@/components/recon/ReservaBadge";
import { RESERVA_OPTIONS } from "@/lib/labels";

const WEEKDAYS = ["L", "M", "M", "J", "V", "S", "D"];
const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

const money0 = (v) => (v == null ? "$0" : `$${Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 })}`);
const money2 = (v) => (v == null ? "—" : `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const pad = (n) => String(n).padStart(2, "0");
const monthKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
const todayKey = () => { const t = new Date(); return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}`; };

const KINDS = [
  { id: "all", label: "Todos", icon: CalendarDays, color: "#2563EB" },
  { id: "delivery", label: "Entregas", icon: Truck, color: "#2563EB" },
  { id: "pickup", label: "Pickups", icon: Package, color: "#7C3AED" },
];
const KIND_TRANSFER = { id: "transfer", label: "Transfers", icon: Repeat, color: "#0D9488" };

const RESERVA_OPTS = [
  { v: "all", l: "Todas" }, { v: "full", l: "Full reservado" },
  { v: "partial", l: "Parcial" }, { v: "none", l: "Sin reservar" },
];
const ORIGEN_OPTS = [
  { v: "all", l: "Todos" }, { v: "bodega", l: "Bodega 11" }, { v: "tiendas", l: "Tiendas" },
];
const PAGO_OPTS = [
  { v: "all", l: "Todos" }, { v: "paid", l: "Full pagado" }, { v: "balance", l: "Con balance" },
];
const SLA_OPTS = [
  { v: "all", l: "Todos" }, { v: "FUERA_SLA", l: "Fuera de SLA" },
  { v: "EN_RIESGO", l: "En riesgo" }, { v: "EN_SLA", l: "En SLA" },
];
const SLA_DAYS = [3, 7, 14, 30, 60, 90, 120];
const NONE = "__none__"; // centinela: selección vacía explícita (quitar todos)

function KpiCard({ label, value, icon: Icon, tone, testid }) {
  const tones = {
    slate: "border-slate-200 bg-slate-50 text-slate-900",
    blue: "border-[#BFDBFE] bg-[#EFF6FF] text-[#1E3A8A]",
    purple: "border-[#DDD6FE] bg-[#F5F3FF] text-[#5B21B6]",
    green: "border-[#A7F3D0] bg-[#ECFDF5] text-[#065F46]",
    teal: "border-[#99F6E4] bg-[#F0FDFA] text-[#115E59]",
  };
  const iconTone = { slate: "text-slate-400", blue: "text-[#2563EB]", purple: "text-[#7C3AED]", green: "text-[#059669]", teal: "text-[#0D9488]" };
  return (
    <div data-testid={testid} className={`rounded-sm border px-4 py-3 ${tones[tone]}`}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.06em] font-semibold opacity-80">
        {Icon && <Icon size={13} className={iconTone[tone]} />} {label}
      </div>
      <div className="font-heading text-2xl font-bold tracking-tight mt-1 tabular-nums">{value}</div>
    </div>
  );
}

function CheckFilter({ trigger, icon: Icon, options, getKey, getLabel, selected, onChange, testidPrefix }) {
  const all = options || [];
  const keys = all.map(getKey);
  const isNone = selected.length === 1 && selected[0] === NONE;
  const allOn = !isNone && (selected.length === 0 || selected.length === keys.length);
  const count = isNone ? 0 : (allOn ? keys.length : selected.length);
  const toggleAll = () => onChange(allOn ? [NONE] : []); // todos <-> ninguno
  const toggle = (k) => {
    const base = isNone ? [] : (selected.length === 0 ? keys : selected);
    const next = base.includes(k) ? base.filter((x) => x !== k) : [...base, k];
    if (next.length === 0) return onChange([NONE]);
    onChange(next.length === keys.length ? [] : next);
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" data-testid={`${testidPrefix}-trigger`}
          className="h-9 rounded-sm border-slate-300 text-slate-600 gap-1.5">
          {Icon ? <Icon size={14} /> : <Filter size={14} />} {trigger}
          {!allOn && (
            <span className="ml-0.5 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-[#2563EB] text-white text-[10px] font-semibold">{count}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 rounded-sm p-0" data-testid={`${testidPrefix}-menu`}>
        <div className="px-3 py-2 border-b border-slate-100">
          <button data-testid={`${testidPrefix}-all`} onClick={toggleAll}
            className="flex items-center gap-2 text-sm font-medium text-slate-700 w-full">
            <span className={`flex items-center justify-center h-4 w-4 rounded-sm border ${allOn ? "bg-[#059669] border-[#059669]" : "border-slate-300"}`}>
              {allOn && <Check size={12} className="text-white" />}
            </span>
            {allOn ? "(Quitar todos)" : "(Seleccionar todos)"}
          </button>
        </div>
        <div className="max-h-72 overflow-auto thin-scroll py-1">
          {all.map((o) => {
            const k = getKey(o);
            const on = allOn || (!isNone && selected.includes(k));
            return (
              <button key={k} data-testid={`${testidPrefix}-${k}`} onClick={() => toggle(k)}
                className="flex items-center gap-2 text-sm text-slate-700 w-full px-3 py-1.5 hover:bg-slate-50 text-left">
                <span className={`flex items-center justify-center h-4 w-4 rounded-sm border shrink-0 ${on ? "bg-[#059669] border-[#059669]" : "border-slate-300"}`}>
                  {on && <Check size={12} className="text-white" />}
                </span>
                <span className="truncate">{getLabel(o)}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function SectionLabel({ icon: Icon, children, disabled, note }) {
  return (
    <div className={`flex items-center justify-between mt-3 mb-1.5 ${disabled ? "opacity-40" : ""}`}>
      <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.06em] font-semibold text-slate-500">
        {Icon && <Icon size={12} />} {children}
      </span>
      {disabled && note && <span className="text-[10px] text-slate-400 italic">{note}</span>}
    </div>
  );
}

function PillRadio({ options, value, onChange, disabled, prefix }) {
  return (
    <div className={`flex flex-wrap gap-1.5 ${disabled ? "opacity-40 pointer-events-none" : ""}`}>
      {options.map((o) => {
        const active = value === o.v;
        return (
          <button key={o.v} data-testid={`${prefix}-${o.v}`} disabled={disabled}
            onClick={() => onChange(o.v)}
            className={`px-2.5 h-7 rounded-sm text-xs font-medium border transition-colors
              ${active ? "bg-[#2563EB] text-white border-[#2563EB]" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
            {o.l}
          </button>
        );
      })}
    </div>
  );
}

function ChecklistSection({ label, icon, options, getKey, getLabel, selected, onChange, prefix }) {
  const all = options || [];
  const keys = all.map(getKey);
  const isNone = selected.length === 1 && selected[0] === NONE;
  const allOn = !isNone && (selected.length === 0 || selected.length === keys.length);
  const count = isNone ? 0 : (allOn ? keys.length : selected.length);
  const toggleAll = () => onChange(allOn ? [NONE] : []); // todos <-> ninguno
  const toggle = (k) => {
    const base = isNone ? [] : (selected.length === 0 ? keys : selected);
    const next = base.includes(k) ? base.filter((x) => x !== k) : [...base, k];
    if (next.length === 0) return onChange([NONE]);
    onChange(next.length === keys.length ? [] : next);
  };
  return (
    <div>
      <SectionLabel icon={icon}>{label}{!allOn && <span className="ml-1 text-[#2563EB]">({count})</span>}</SectionLabel>
      <div className="max-h-36 overflow-auto thin-scroll rounded-sm border border-slate-200">
        {all.length === 0 ? (
          <div className="px-2.5 py-2 text-xs text-slate-400">Sin opciones</div>
        ) : (
          <>
            <button data-testid={`${prefix}-all`} onClick={toggleAll}
              className="sticky top-0 z-10 flex items-center gap-2 text-sm font-medium text-slate-700 w-full px-2.5 py-1.5 bg-slate-50 border-b border-slate-200 hover:bg-slate-100 text-left">
              <span className={`flex items-center justify-center h-4 w-4 rounded-sm border shrink-0 ${allOn ? "bg-[#059669] border-[#059669]" : "border-slate-300"}`}>
                {allOn && <Check size={12} className="text-white" />}
              </span>
              {allOn ? "(Quitar todos)" : "(Seleccionar todos)"}
            </button>
            {all.map((o) => {
              const k = getKey(o);
              const on = allOn || (!isNone && selected.includes(k));
              return (
                <button key={k} data-testid={`${prefix}-${k}`} onClick={() => toggle(k)}
                  className="flex items-center gap-2 text-sm text-slate-700 w-full px-2.5 py-1.5 hover:bg-slate-50 text-left">
                  <span className={`flex items-center justify-center h-4 w-4 rounded-sm border shrink-0 ${on ? "bg-[#059669] border-[#059669]" : "border-slate-300"}`}>
                    {on && <Check size={12} className="text-white" />}
                  </span>
                  <span className="truncate">{getLabel(o)}</span>
                </button>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}

function AdvancedFilters({ isRoutes, storeOpts, saleOpts, statusOpts, f, set, count, onClear }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" data-testid="cal-filters-trigger"
          className="h-9 rounded-sm border-slate-300 text-slate-600 gap-1.5">
          <SlidersHorizontal size={14} /> Filtros
          {count > 0 && (
            <span className="ml-0.5 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-[#2563EB] text-white text-[10px] font-semibold">{count}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 rounded-sm p-3 max-h-[78vh] overflow-auto thin-scroll" data-testid="cal-filters-menu">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-slate-800">Filtros avanzados</span>
          {count > 0 && (
            <button data-testid="cal-filters-clear" onClick={onClear}
              className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1">
              <X size={12} /> Limpiar
            </button>
          )}
        </div>

        <SectionLabel icon={Search}>Búsqueda</SectionLabel>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="cal-fsearch" value={f.search}
            onChange={(e) => set("search", e.target.value)}
            placeholder="Order #, cliente, modelo…"
            className="h-9 rounded-sm border-slate-300 bg-white text-sm pl-8" />
        </div>

        <ChecklistSection label="Tienda" icon={Store} options={storeOpts}
          getKey={(o) => o.code} getLabel={(o) => `${o.code}${o.name ? ` · ${o.name}` : ""} (${o.count})`}
          selected={f.stores} onChange={(v) => set("stores", v)} prefix="cal-fstore" />

        <ChecklistSection label="Tipo de venta" icon={ShoppingBag} options={saleOpts}
          getKey={(o) => o} getLabel={(o) => o}
          selected={f.saleTypes} onChange={(v) => set("saleTypes", v)} prefix="cal-fsale" />

        {isRoutes ? (
          <ChecklistSection label="Estatus" icon={ClipboardCheck} options={statusOpts}
            getKey={(o) => o} getLabel={(o) => o}
            selected={f.status} onChange={(v) => set("status", v)} prefix="cal-fstatus" />
        ) : (
          <>
            <SectionLabel icon={ClipboardCheck} disabled note="Solo Por ruta">Estatus</SectionLabel>
            <div className="rounded-sm border border-slate-200 px-2.5 py-2 text-xs text-slate-400 italic">
              Disponible en modo Por ruta (reporte)
            </div>
          </>
        )}

        {isRoutes && (
          <ChecklistSection label="Estado de reserva" icon={Warehouse} options={RESERVA_OPTIONS}
            getKey={(o) => o.value} getLabel={(o) => o.label}
            selected={f.reservaStatus} onChange={(v) => set("reservaStatus", v)} prefix="cal-freserva-status" />
        )}

        <SectionLabel icon={Timer}>SLA</SectionLabel>
        <PillRadio options={SLA_OPTS} value={f.sla} onChange={(v) => set("sla", v)} prefix="cal-fsla" />
        {f.sla !== "all" && (
          <div className="mt-2">
            <span className="text-[10px] uppercase tracking-wide text-slate-400">Horizonte (días)</span>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {SLA_DAYS.map((d) => (
                <button key={d} data-testid={`cal-fsladays-${d}`} onClick={() => set("slaDays", d)}
                  className={`px-2 h-6 rounded-sm text-[11px] font-medium border transition-colors
                    ${f.slaDays === d ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
                  {d}d
                </button>
              ))}
            </div>
          </div>
        )}

        <SectionLabel icon={PackageCheck}>Reserva</SectionLabel>
        <PillRadio options={RESERVA_OPTS} value={f.reserva} onChange={(v) => set("reserva", v)} prefix="cal-freserva" />

        <SectionLabel icon={Warehouse} disabled={!isRoutes} note="Solo Por ruta">Origen de reserva</SectionLabel>
        <PillRadio options={ORIGEN_OPTS} value={isRoutes ? f.reservaOrigen : "all"} disabled={!isRoutes}
          onChange={(v) => set("reservaOrigen", v)} prefix="cal-forigen" />

        <SectionLabel icon={CreditCard} disabled={isRoutes} note="Solo Por entrega">Pago</SectionLabel>
        <PillRadio options={PAGO_OPTS} value={isRoutes ? "all" : f.pago} disabled={isRoutes}
          onChange={(v) => set("pago", v)} prefix="cal-fpago" />

        <SectionLabel icon={DollarSign} disabled={!isRoutes} note="Solo Por ruta">COD (contra entrega)</SectionLabel>
        <button data-testid="cal-fcod" disabled={!isRoutes} onClick={() => set("cod", !f.cod)}
          className={`flex items-center gap-2 text-sm w-full ${!isRoutes ? "opacity-40 pointer-events-none" : "text-slate-700"}`}>
          <span className={`flex items-center justify-center h-4 w-4 rounded-sm border ${isRoutes && f.cod ? "bg-[#059669] border-[#059669]" : "border-slate-300"}`}>
            {isRoutes && f.cod && <Check size={12} className="text-white" />}
          </span>
          Solo COD (estatus D2)
        </button>
      </PopoverContent>
    </Popover>
  );
}

function DayCell({ dateKey, dayNum, cell, max, isToday, onClick, source }) {
  const lines = cell?.lines || 0;
  const ratio = max > 0 ? lines / max : 0;
  const dark = ratio > 0.55;
  const bg = lines > 0 ? `rgba(37, 99, 235, ${(0.08 + 0.6 * ratio).toFixed(3)})` : "#FFFFFF";
  const tip = source === "routes"
    ? `${lines} líneas · ${cell?.orders || 0} pedidos · ${cell?.routes || 0} rutas`
    : `${lines} líneas · ${cell?.orders || 0} pedidos · ${money0(cell?.amount)}`;
  return (
    <button
      data-testid={`cal-day-${dateKey}`}
      onClick={() => lines > 0 && onClick(dateKey)}
      disabled={lines === 0}
      style={{ backgroundColor: bg }}
      className={`group relative min-h-[92px] sm:min-h-[104px] p-2 text-left border border-slate-100 rounded-sm transition-all
        ${lines > 0 ? "cursor-pointer hover:ring-2 hover:ring-[#2563EB]/40" : "cursor-default"}
        ${isToday ? "ring-2 ring-[#F59E0B]" : ""}`}>
      <span className={`text-sm font-semibold ${dark ? "text-white" : lines > 0 ? "text-slate-800" : "text-slate-300"}`}>{dayNum}</span>
      {lines > 0 && (
        <div className="absolute left-2 bottom-1.5 right-2">
          <div className={`font-heading text-lg font-bold leading-none ${dark ? "text-white" : "text-slate-900"}`}>{lines}</div>
          <div className={`mt-0.5 flex items-center gap-2 text-[11px] font-medium ${dark ? "text-white/90" : "text-slate-500"}`}>
            {source === "routes" ? (
              <>
                <span className="inline-flex items-center gap-0.5"><RouteIcon size={11} /> {cell.routes}</span>
                <span className="inline-flex items-center gap-0.5"><Repeat size={11} /> {cell.transfers}</span>
              </>
            ) : (
              <>
                <span className="inline-flex items-center gap-0.5"><Truck size={11} /> {cell.deliveries}</span>
                <span className="inline-flex items-center gap-0.5"><Package size={11} /> {cell.pickups}</span>
              </>
            )}
          </div>
        </div>
      )}
      {lines > 0 && (
        <div className="pointer-events-none absolute z-30 top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity
          bg-[#0F172A] text-white text-[11px] rounded-sm px-2 py-1 whitespace-nowrap shadow-lg">
          {tip}
        </div>
      )}
    </button>
  );
}

function DaySheet({ dateKey, source, params, open, onClose }) {
  const { data, isLoading } = useQuery({
    queryKey: ["calendar-day", source, dateKey, params],
    queryFn: async () => {
      const ep = source === "routes" ? "/recon/calendar/routes/day" : "/recon/calendar/day";
      return (await api.get(ep, { params: { date: dateKey, ...params } })).data;
    },
    enabled: !!dateKey && open,
  });
  const items = data?.items || [];
  const pretty = dateKey ? (() => {
    const [y, m, d] = dateKey.split("-").map(Number);
    return `${d} de ${MONTHS[m - 1]} ${y}`;
  })() : "";
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto thin-scroll p-0" data-testid="cal-day-sheet">
        <SheetHeader className="px-5 pt-5 pb-4 border-b border-[#E2E8F0]">
          <SheetTitle className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
            <CalendarDays size={20} className="text-[#2563EB]" /> {pretty}
          </SheetTitle>
          <SheetDescription className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-1 text-slate-600">
            {data ? (
              <>
                <span><span className="font-mono font-semibold text-slate-900">{data.total}</span> líneas</span>
                <span><span className="font-mono font-semibold text-slate-900">{data.orders}</span> pedidos</span>
                {source === "routes"
                  ? null
                  : <span className="text-[#065F46]"><span className="font-mono font-semibold">{money2(data.amount)}</span> comprometido</span>}
              </>
            ) : <span>Cargando líneas del día…</span>}
          </SheetDescription>
        </SheetHeader>
        <div className="px-2 py-2 overflow-x-auto thin-scroll">
          {isLoading ? (
            <div className="py-16 text-center text-slate-400"><Loader2 className="animate-spin inline" size={20} /></div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-slate-400"><Inbox className="inline mb-2" size={26} /><div className="text-sm">Sin líneas para este día</div></div>
          ) : source === "routes" ? (
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left border-b border-[#E2E8F0]">
                  {["Order #", "Modelo", "Ruta", "Destino", "Origen", "Tipo", "Reserva", "Estatus"].map((h) => (
                    <th key={h} className="px-3 py-2 text-[11px] uppercase tracking-[0.05em] font-semibold text-slate-500 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((r, i) => (
                  <tr key={r.id} data-testid={`cal-day-row-${r.id}`}
                    className={`border-b border-slate-100 ${i % 2 ? "bg-slate-50/40" : "bg-white"}`}>
                    <td className="px-3 py-2 font-mono text-slate-900 whitespace-nowrap">{r.order_number || "—"}</td>
                    <td className="px-3 py-2 font-mono text-slate-600 whitespace-nowrap">{r.model_number || "—"}</td>
                    <td className="px-3 py-2 font-mono text-slate-700 whitespace-nowrap" title={r.route_name || ""}>{r.route || "—"}</td>
                    <td className="px-3 py-2 text-slate-700 max-w-[140px] truncate">{r.ship_to_name || "—"}</td>
                    <td className="px-3 py-2 text-slate-600 max-w-[120px] truncate" title={r.ship_from_location || ""}>{r.ship_from_name || r.ship_from_location || "—"}</td>
                    <td className="px-3 py-2">
                      <OrderTypeBadge orderType={r.order_type} fallback={r.delivery_type} />
                    </td>
                    <td className="px-3 py-2"><ReservaBadge code={r.reserva_status} /></td>
                    <td className="px-3 py-2"><StatusBadge code={r.match_status} reasons={r.mismatch_reasons} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left border-b border-[#E2E8F0]">
                  {["Order #", "Modelo", "Cliente", "Tipo", "Origen", "Qty", "Monto"].map((h) => (
                    <th key={h} className="px-3 py-2 text-[11px] uppercase tracking-[0.05em] font-semibold text-slate-500 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((r, i) => (
                  <tr key={r.id} data-testid={`cal-day-row-${r.id}`}
                    className={`border-b border-slate-100 ${i % 2 ? "bg-slate-50/40" : "bg-white"}`}>
                    <td className="px-3 py-2 font-mono text-slate-900 whitespace-nowrap">{r.order_number || "—"}</td>
                    <td className="px-3 py-2 font-mono text-slate-600 whitespace-nowrap">{r.model_number || "—"}</td>
                    <td className="px-3 py-2 text-slate-700 max-w-[150px] truncate">{r.ship_to_name || "—"}</td>
                    <td className="px-3 py-2">
                      <OrderTypeBadge orderType={r.order_type} fallback={r.kind === "pickup" ? "Pickup" : "Entrega"} />
                    </td>
                    <td className="px-3 py-2 text-slate-600 max-w-[140px] truncate" title={r.ship_from_location || ""}>{r.ship_from_name || r.ship_from_location || "—"}</td>
                    <td className="px-3 py-2 font-mono text-slate-600">{r.qty_ordered ?? "—"}</td>
                    <td className="px-3 py-2 font-mono text-slate-700 whitespace-nowrap">{money2(r.order_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

const SOURCES = [
  { id: "view", label: "Por entrega", icon: CalendarDays },
  { id: "routes", label: "Por ruta", icon: RouteIcon },
];

const INITIAL_ADV = { stores: [], saleTypes: [], reserva: "all", reservaOrigen: "all", pago: "all", cod: false, search: "", status: [], sla: "all", slaDays: 7, reservaStatus: [] };

export function CalendarPanel() {
  const [source, setSource] = useState("view");
  const [view, setView] = useState("calendar");
  const [month, setMonth] = useState(() => { const t = new Date(); return new Date(t.getFullYear(), t.getMonth(), 1); });
  const [kind, setKind] = useState("all");
  const [routesSel, setRoutesSel] = useState([]);
  const [adv, setAdv] = useState(INITIAL_ADV);
  const [selectedDay, setSelectedDay] = useState(null);

  const isRoutes = source === "routes";
  const mKey = monthKey(month);
  const setAdvKey = (k, v) => setAdv((a) => ({ ...a, [k]: v }));
  const switchSource = (s) => { setSource(s); if (s === "view" && kind === "transfer") setKind("all"); };

  const { data: opts } = useQuery({
    queryKey: ["calendar-options"],
    queryFn: async () => (await api.get("/recon/calendar/options")).data,
  });
  const { data: routeOpts } = useQuery({
    queryKey: ["calendar-route-options"],
    queryFn: async () => (await api.get("/recon/calendar/routes/options")).data,
    enabled: isRoutes,
  });

  const storeOpts = isRoutes ? (routeOpts?.stores || []) : (opts?.stores || []);
  const saleOpts = isRoutes ? (routeOpts?.sale_types || []) : (opts?.sale_types || []);
  const statusOpts = isRoutes ? (routeOpts?.statuses || []) : [];

  // Build query params shared by month grid + day drill-down
  const queryParams = useMemo(() => {
    const p = { kind };
    if (adv.stores.length) p.stores = adv.stores.join(",");
    if (adv.saleTypes.length) p.sale_types = adv.saleTypes.join(",");
    if (adv.reserva !== "all") p.reserva = adv.reserva;
    if (adv.search.trim()) p.search = adv.search.trim();
    if (adv.sla !== "all") { p.sla = adv.sla; p.sla_days = adv.slaDays; }
    if (isRoutes) {
      if (routesSel.length) p.routes = routesSel.join(",");
      if (adv.reservaOrigen !== "all") p.reserva_origen = adv.reservaOrigen;
      if (adv.cod) p.cod = true;
      if (adv.status.length) p.status = adv.status.join(",");
      if (adv.reservaStatus.length) p.reserva_status = adv.reservaStatus.join(",");
    } else {
      if (adv.pago !== "all") p.pago = adv.pago;
    }
    return p;
  }, [kind, adv, routesSel, isRoutes]);

  const { data, isFetching } = useQuery({
    queryKey: ["calendar", source, mKey, queryParams],
    queryFn: async () => {
      const ep = isRoutes ? "/recon/calendar/routes" : "/recon/calendar";
      return (await api.get(ep, { params: { month: mKey, ...queryParams } })).data;
    },
    placeholderData: keepPreviousData,
  });

  const days = data?.days || {};
  const totals = data?.totals || {};
  const maxLines = useMemo(() => Math.max(0, ...Object.values(days).map((d) => d.lines || 0)), [days]);

  const advCount = useMemo(() => {
    let n = 0;
    if (adv.stores.length) n++;
    if (adv.saleTypes.length) n++;
    if (adv.reserva !== "all") n++;
    if (adv.search.trim()) n++;
    if (adv.sla !== "all") n++;
    if (isRoutes) { if (adv.reservaOrigen !== "all") n++; if (adv.cod) n++; if (adv.status.length) n++; if (adv.reservaStatus.length) n++; }
    else if (adv.pago !== "all") n++;
    return n;
  }, [adv, isRoutes]);

  const grid = useMemo(() => {
    const y = month.getFullYear(), m = month.getMonth();
    const first = new Date(y, m, 1);
    const lead = (first.getDay() + 6) % 7;
    const count = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < lead; i++) cells.push(null);
    for (let d = 1; d <= count; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [month]);

  const tKey = todayKey();
  const sortedDays = useMemo(() => Object.entries(days).sort((a, b) => a[0].localeCompare(b[0])), [days]);
  const goToday = () => { const t = new Date(); setMonth(new Date(t.getFullYear(), t.getMonth(), 1)); };
  const shift = (n) => setMonth((d) => new Date(d.getFullYear(), d.getMonth() + n, 1));
  const kinds = isRoutes ? [...KINDS, KIND_TRANSFER] : KINDS;

  return (
    <div className="space-y-4" data-testid="calendar-panel">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <CalendarDays size={20} className="text-[#2563EB]" /> Calendario de entregas
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {isRoutes ? "Líneas del reporte STORIS agendadas por fecha y ruta" : "Líneas agendadas por fecha de entrega (Est Date)"}
          </p>
        </div>
        <div className="inline-flex rounded-sm border border-slate-300 overflow-hidden bg-white">
          <button data-testid="cal-view-list" onClick={() => setView("list")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm ${view === "list" ? "bg-[#0F172A] text-white" : "text-slate-600 hover:bg-slate-50"}`}>
            <List size={15} /> Lista
          </button>
          <button data-testid="cal-view-calendar" onClick={() => setView("calendar")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm ${view === "calendar" ? "bg-[#0F172A] text-white" : "text-slate-600 hover:bg-slate-50"}`}>
            <CalendarDays size={15} /> Calendario
          </button>
        </div>
      </div>

      {/* Source toggle */}
      <div className="inline-flex rounded-sm border border-slate-300 overflow-hidden bg-white" data-testid="cal-source-toggle">
        {SOURCES.map((s) => {
          const Icon = s.icon;
          const active = source === s.id;
          return (
            <button key={s.id} data-testid={`cal-source-${s.id}`} onClick={() => switchSource(s.id)}
              className={`flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium ${active ? "bg-[#2563EB] text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              <Icon size={15} /> {s.label}
            </button>
          );
        })}
      </div>

      {/* Source explainer — clarifies where pickups live (TE325 vs TE331) */}
      <div
        data-testid="cal-source-note"
        className={`flex items-start gap-2.5 rounded-sm border px-3.5 py-2.5 text-[13px] leading-snug ${
          isRoutes
            ? "border-[#A7F3D0] bg-[#ECFDF5] text-[#065F46]"
            : "border-[#FDE68A] bg-[#FFFBEB] text-[#92400E]"
        }`}
      >
        <Info size={16} className="mt-0.5 shrink-0" />
        {isRoutes ? (
          <span>
            Vista <strong>Por ruta</strong> · alimentada por el <strong>Reporte STORIS (TE331)</strong>.
            Aquí <strong>sí aparecen todos los Pickups</strong> (recogidas del cliente), tengan o no ruta de camión asignada.
          </span>
        ) : (
          <span>
            Vista <strong>Por entrega</strong> · alimentada por la <strong>Vista actual (TE325)</strong>, que casi no contiene pickups.
            Para ver los <strong>Pickups</strong> (recogidas), cambia a la pestaña{" "}
            <button
              data-testid="cal-source-note-switch"
              onClick={() => switchSource("routes")}
              className="underline font-semibold hover:text-[#78350F]"
            >
              «Por ruta»
            </button>{" "}
            (Reporte TE331).
          </span>
        )}
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-sm p-4 space-y-4">
        {/* Toolbar */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" data-testid="cal-prev" onClick={() => shift(-1)}
              className="h-9 w-9 p-0 rounded-sm border-slate-300"><ChevronLeft size={16} /></Button>
            <span data-testid="cal-month-label" className="font-heading text-lg font-bold tracking-tight text-slate-900 min-w-[150px] text-center">
              {MONTHS[month.getMonth()]} {month.getFullYear()}
            </span>
            <Button variant="outline" size="sm" data-testid="cal-next" onClick={() => shift(1)}
              className="h-9 w-9 p-0 rounded-sm border-slate-300"><ChevronRight size={16} /></Button>
            <Button variant="outline" size="sm" data-testid="cal-today" onClick={goToday}
              className="h-9 rounded-sm border-slate-300 text-slate-600">Hoy</Button>
            {isFetching && <Loader2 className="animate-spin text-slate-300" size={16} />}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-sm border border-slate-300 overflow-hidden bg-white">
              {kinds.map((k) => {
                const Icon = k.icon;
                const active = kind === k.id;
                return (
                  <button key={k.id} data-testid={`cal-kind-${k.id}`} onClick={() => setKind(k.id)}
                    style={active ? { color: k.color, backgroundColor: `${k.color}14` } : {}}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium ${active ? "" : "text-slate-500 hover:bg-slate-50"}`}>
                    <Icon size={14} style={{ color: k.color }} /> {k.label}
                  </button>
                );
              })}
            </div>
            {isRoutes && (
              <CheckFilter trigger="Ruta" icon={RouteIcon} options={routeOpts?.routes} selected={routesSel} onChange={setRoutesSel}
                getKey={(o) => o.route} getLabel={(o) => `${o.route}${o.name ? ` · ${o.name}` : ""} (${o.count})`}
                testidPrefix="cal-route" />
            )}
            <AdvancedFilters isRoutes={isRoutes} storeOpts={storeOpts} saleOpts={saleOpts} statusOpts={statusOpts}
              f={adv} set={setAdvKey} count={advCount} onClear={() => setAdv(INITIAL_ADV)} />
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {isRoutes ? (
            <>
              <KpiCard testid="cal-kpi-orders" label="Pedidos" value={(totals.orders || 0).toLocaleString()} icon={ShoppingBag} tone="slate" />
              <KpiCard testid="cal-kpi-lines" label="Líneas" value={(totals.lines || 0).toLocaleString()} icon={CalendarDays} tone="blue" />
              <KpiCard testid="cal-kpi-routes" label="Rutas" value={(totals.routes || 0).toLocaleString()} icon={RouteIcon} tone="teal" />
              <KpiCard testid="cal-kpi-transfers" label="Transfers" value={(totals.transfers || 0).toLocaleString()} icon={Repeat} tone="purple" />
            </>
          ) : (
            <>
              <KpiCard testid="cal-kpi-orders" label="Pedidos" value={(totals.orders || 0).toLocaleString()} icon={ShoppingBag} tone="slate" />
              <KpiCard testid="cal-kpi-deliveries" label="Entregas" value={(totals.deliveries || 0).toLocaleString()} icon={Truck} tone="blue" />
              <KpiCard testid="cal-kpi-pickups" label="Pickups" value={(totals.pickups || 0).toLocaleString()} icon={Package} tone="purple" />
              <KpiCard testid="cal-kpi-amount" label="Comprometido" value={money0(totals.amount)} icon={DollarSign} tone="green" />
            </>
          )}
        </div>

        {view === "calendar" ? (
          <div data-testid="cal-grid">
            <div className="grid grid-cols-7 gap-1.5 mb-1.5">
              {WEEKDAYS.map((w, i) => (
                <div key={i} className="text-center text-[11px] uppercase tracking-[0.08em] font-semibold text-slate-400 py-1">{w}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1.5">
              {grid.map((d, i) => {
                if (d === null) return <div key={`e${i}`} className="min-h-[92px] sm:min-h-[104px] rounded-sm bg-slate-50/40" />;
                const dk = `${mKey}-${pad(d)}`;
                return (
                  <DayCell key={dk} dateKey={dk} dayNum={d} cell={days[dk]} max={maxLines}
                    isToday={dk === tKey} onClick={setSelectedDay} source={source} />
                );
              })}
            </div>
          </div>
        ) : (
          <div className="border border-[#E2E8F0] rounded-sm overflow-hidden" data-testid="cal-list">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-[#E2E8F0] text-left">
                  {(isRoutes
                    ? ["Fecha", "Líneas", "Rutas", "Pedidos", "Transfers"]
                    : ["Fecha", "Líneas", "Entregas", "Pickups", "Pedidos", "Comprometido"]
                  ).map((h) => (
                    <th key={h} className="px-3 py-2.5 text-[11px] uppercase tracking-[0.05em] font-semibold text-slate-500 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedDays.length === 0 ? (
                  <tr><td colSpan={6} className="py-16 text-center text-slate-400">
                    <Inbox className="inline mb-2" size={26} /><div className="text-sm">Sin entregas agendadas este mes</div>
                  </td></tr>
                ) : sortedDays.map(([dk, c]) => {
                  const [y, m, d] = dk.split("-").map(Number);
                  return (
                    <tr key={dk} data-testid={`cal-list-row-${dk}`} onClick={() => setSelectedDay(dk)}
                      className={`border-b border-slate-100 cursor-pointer hover:bg-slate-100 ${dk === tKey ? "bg-[#FFFBEB]" : ""}`}>
                      <td className="px-3 py-2 text-slate-800 whitespace-nowrap">
                        <span className="font-semibold">{d}</span> {MONTHS[m - 1].slice(0, 3)} {y}
                        <span className="text-slate-400 ml-1.5 text-xs">{WEEKDAYS[(new Date(y, m - 1, d).getDay() + 6) % 7]}</span>
                      </td>
                      <td className="px-3 py-2 font-mono font-semibold text-slate-900">{c.lines}</td>
                      {isRoutes ? (
                        <>
                          <td className="px-3 py-2 font-mono text-[#115E59]">{c.routes}</td>
                          <td className="px-3 py-2 font-mono text-slate-600">{c.orders}</td>
                          <td className="px-3 py-2 font-mono text-[#5B21B6]">{c.transfers}</td>
                        </>
                      ) : (
                        <>
                          <td className="px-3 py-2 font-mono text-[#1E3A8A]">{c.deliveries}</td>
                          <td className="px-3 py-2 font-mono text-[#5B21B6]">{c.pickups}</td>
                          <td className="px-3 py-2 font-mono text-slate-600">{c.orders}</td>
                          <td className="px-3 py-2 font-mono text-[#065F46] whitespace-nowrap">{money2(c.amount)}</td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <DaySheet dateKey={selectedDay} source={source} params={queryParams}
        open={!!selectedDay} onClose={() => setSelectedDay(null)} />
    </div>
  );
}
